import {Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges} from '@angular/core';
import {ColFileStructure, FieldTable, EtlDesignDTO} from '@tj/tj-dmp-lib-api';
import {SelectItem} from 'primeng/api';
import {FieldMapping} from '../field-mapping.interface';
import {EtlUpdateDesignService} from '../etl-update-design.service';
import {TargetFieldDestination, UtmRuleConfig} from '../target-field-destination.interface';
import {TranslateService} from '@ngx-translate/core';
import {EtlI18nConversionService} from '../etl-i18n-conversion.service';
import {emptyI18nConversion, I18nConversionConfig} from '../i18n-conversion.interface';

@Component({
    // tslint:disable-next-line:component-selector
    selector: 'dmp-etl-popup-design-param',
    templateUrl: './etl-popup-design-param.component.html',
    styleUrls: ['./etl-popup-design-param.component.scss'],
    standalone: false
})
export class EtlPopupDesignParamComponent implements OnInit, OnChanges {

    @Input() public fields: TargetFieldDestination[];
    @Input() public tenant: string;
    @Input() public etl: EtlDesignDTO;
    @Input() public onlyTable: string;
    @Input() public onlyUtmFields = false;
    @Input() public listeCodes: SelectItem[];
    @Input() public isAuthorisedAdmin: boolean = false;
    @Output() public closePopup = new EventEmitter<any>();
    @Output() public modif = new EventEmitter<TargetFieldDestination[]>();
    @Output() public onDelete = new EventEmitter<TargetFieldDestination[]>();

    listDelete: TargetFieldDestination[] = [];

    displayParamField = false;
    patternsDate: SelectItem[];

    validate = false;
    fieldSource: any = null;
    public modeCreationParDestination = false;
    traductionsAiriane: SelectItem[];
    utmPatterns: SelectItem[];
    utmTypes: SelectItem[];
    utmModes: SelectItem[];
    fieldGroups: Array<{ key: string; label: string; fields: TargetFieldDestination[] }> = [];
    useUtmGrouping = false;

    constructor(
        private etlUpdateDesignService: EtlUpdateDesignService,
        private i18nConversion: EtlI18nConversionService,
        // tslint:disable-next-line:no-shadowed-variable
        private translate: TranslateService) {
        this.patternsDate = [];
        ['yyyy-MM-dd', 'dd/MM/yyyy HH:mm', 'HH:mm'].forEach(p => {
            this.patternsDate.push({
                label: p,
                value: p
            });
        });
        //
        this.traductionsAiriane = [];
        ['AIRPLANE', 'AIRLINE', 'AIRPORT', 'CITY', 'TERRITORY', 'COUNTRY', 'CONTINENT'].forEach(code => {
            this.traductionsAiriane.push({label: this.translate.instant(`etl.common.`+code), value: code});
        });
        this.utmPatterns = [
            { label: 'Misterfly Pattern 1', value: 'MISTERFLY_PATTERN_1' },
            { label: 'Misterfly Pattern 2', value: 'MISTERFLY_PATTERN_2' }
        ];
        this.utmTypes = [
            { label: 'Source (src.)', value: 'SOURCE' },
            { label: 'Medium (med.)', value: 'MEDIUM' },
            { label: 'Campaign (cmp.)', value: 'CAMPAIGN' },
            { label: 'Term (trm.)', value: 'TERM' },
            { label: 'Content (cnt.)', value: 'CONTENT' }
        ];
        this.utmModes = [
            { label: 'Preset rule', value: 'PRESET' },
            { label: 'Custom regex', value: 'CUSTOM' },
            { label: 'Texte brut', value: 'DIRECT' }
        ];
    }

    ngOnInit(): void {
        this.updateValidate();
    }

    updateValidate(event = null): void {
        const hasInvalidBasic = (this.fields || []).findIndex(f =>
            f.fieldDest == null ||
            f.tableDest == null ||
            (f.meta?.type === 'Date' || f.meta?.type === 'DateTime') && f.pattern?.length === 0
        );
        if (hasInvalidBasic !== -1) {
            this.validate = false;
            return;
        }
        const hasInvalidUtm = (this.fields || []).some((f) => {
            if (!f.utmMatching || !this.isUtmTargetField(f)) {
                return false;
            }
            const rules = Array.isArray(f.utmRules) ? f.utmRules : [];
            if (rules.length === 0) {
                return true;
            }
            return rules.some((rule) => {
                if (rule?.regexMode === 'DIRECT') {
                    return false;
                }
                if (rule?.regexMode !== 'PRESET') {
                    return !(rule?.regex && rule.regex.trim().length > 0);
                }
                return !rule.utmPattern || !rule.utmType;
            });
        });
        const hasInvalidDictionary = (this.fields || []).some(field => field.isConvertByDico &&
            (field.reverseSearch || field.utmMatching || !this.isStringType(field) ||
                !this.i18nConversion.isValid(this.tenant, field.i18nConversion)));
        this.validate = !!this.fields?.length && !hasInvalidUtm && !hasInvalidDictionary;
    }

    onDictionaryConversionChange(field: TargetFieldDestination): void {
        if (field.isConvertByDico) {
            field.i18nConversion = this.i18nConversion.normalize(this.tenant, field.i18nConversion || emptyI18nConversion());
        }
        this.updateValidate();
    }

    onDictionaryConfigurationChange(field: TargetFieldDestination, config: I18nConversionConfig): void {
        field.i18nConversion = this.i18nConversion.normalize(this.tenant, config);
        this.updateValidate();
    }

    ngOnChanges(changes: SimpleChanges): void {
        this.update(changes.fields?.currentValue != null);
    }

    update(changeField = false) {
        this.displayParamField = this.fields != null && this.fields.length > 0;
        if (changeField) {
            this.listDelete = [];

            this.fields.forEach(field => {
                if (this.useUtmGrouping) {
                    field.utmMatching = true;
                }
                field.replaceRegex = field.replaceRegex || [];
                field.replaceValue = field.replaceValue || [];
                field.replaceAll = field.replaceAll || [];

                const diff = field.replaceRegex.length - field.replaceAll.length;
                if (diff > 0) {
                    for (let i = 0; i < diff; i++) {
                        field.replaceAll.push(false);
                    }
                }

                if (field.replaceRegex.length === 0) {
                    field.replaceRegex.push('');
                    field.replaceValue.push('');
                    field.replaceAll.push(false);
                }
                field.utmMatching = field.utmMatching ?? false;
                field.utmPattern = field.utmPattern ?? null;
                field.utmRegex = field.utmRegex || '';
                field.utmRegexMode = field.utmRegexMode || (field.utmRegex ? 'CUSTOM' : 'PRESET');
                field.utmKeepRawOnNoMatch = field.utmKeepRawOnNoMatch ?? false;
                this.ensureUtmRules(field);
                if (field.isConvertByDico && !field.i18nConversion) {
                    field.i18nConversion = emptyI18nConversion();
                }
            });
            // mode source non setté
            this.modeCreationParDestination = this.fields.length === 1 && this.fields[0].code == null;
            if (this.modeCreationParDestination) {
                this.fieldSource = this.etl.cols[0];
                this.setField(this.fieldSource);
            }
        }
        this.rebuildFieldGroups();
        this.updateValidate();
    }

    isDateTimeType(destination) {
        return this.etlUpdateDesignService.isDateTimeType(destination);
    }

    getFieldType(destination) {
        return this.etlUpdateDesignService.getFieldType(destination);
    }

    isStringType(destination) {
        return this.etlUpdateDesignService.isStringType(destination);
    }

    isDecimalType(field: TargetFieldDestination) {
        return this.etlUpdateDesignService.isDecimalType(field);
    }

    updateMapping(field: TargetFieldDestination, mapping: FieldMapping) {
        field.fieldDest = mapping.fieldDest;
        field.tableDest = mapping.tableDest;
        field.meta = mapping.field;
        const isUtmTarget = this.isUtmTargetField(field);
        if (isUtmTarget || !this.isStringType(field)) {
            field.isConvertByDico = false;
        }
        if (isUtmTarget) {
            // UTM destination fields always use UTM matching.
            field.utmMatching = true;
            this.clearLegacyReplace(field);
            field.utmRegexMode = field.utmRegexMode || 'PRESET';
            if (!field.utmPattern && this.utmPatterns.length === 1) {
                field.utmPattern = this.utmPatterns[0].value as 'MISTERFLY_PATTERN_1' | 'MISTERFLY_PATTERN_2';
            }
        }
        if (!isUtmTarget && !this.useUtmGrouping) {
            // If destination is no longer a UTM field, disable UTM matching and restore other transforms.
            field.utmMatching = false;
        }
        if (field.utmMatching && field.utmRegexMode === 'PRESET') {
            field.utmType = this.resolveUtmTypeByFieldDest(field.utmPattern, field.fieldDest) || field.utmType;
        }
        this.ensureUtmRules(field);
        this.updateValidate();
    }

    onHideDialogParamField(event): void {
        this.closePopup.emit();
    }

    onShowDialogParamField(event): void {
        this.updateValidate();
    }

    doCancel(): void {
        this.closePopup.emit();
    }

    doValidateAndClose(): void {
        this.updateValidate();
        // Removing the last mapping is still allowed to close and emit the deletion.
        if (this.fields?.length && !this.validate) {
            return;
        }
        this.fields.forEach(field => {
            if (this.useUtmGrouping) {
                field.utmMatching = true;
            }
            if (!field.replaceRegex) field.replaceRegex = [];
            if (!field.replaceValue) field.replaceValue = [];
            if (!field.replaceAll) field.replaceAll = [];

            field.replace = field.replace ?? false;
            field.utmMatching = field.utmMatching ?? false;
            field.utmPattern = field.utmPattern ?? null;
            field.utmRegex = field.utmRegex || '';
            field.utmRegexMode = field.utmRegexMode || (field.utmRegex ? 'CUSTOM' : 'PRESET');
            field.utmKeepRawOnNoMatch = field.utmKeepRawOnNoMatch ?? false;
            if (field.utmMatching && this.isUtmTargetField(field)) {
                this.clearLegacyReplace(field);
            }
            this.ensureUtmRules(field);
        });
        this.modif.emit(this.fields);
        this.onDelete.emit(this.listDelete);
        this.closePopup.emit();
    }

    doDelete(field: TargetFieldDestination, index?: number) {
        this.listDelete.push(field);
        const deleteIndex = typeof index === 'number' ? index : this.fields.indexOf(field);
        if (deleteIndex >= 0) {
            this.fields.splice(deleteIndex, 1);
            this.fields = [...this.fields];
        } else {
            this.fields = this.fields.filter(f => f !== field);
        }
        if (this.fields.length === 0) {
            this.doValidateAndClose();
            return;
        }
        this.rebuildFieldGroups();
        this.updateValidate();
    }

    private clearLegacyReplace(field: TargetFieldDestination): void {
        field.replace = false;
        field.replaceRegex = [''];
        field.replaceValue = [''];
        field.replaceAll = [false];
    }

    addUtmMappingFromSource(field: TargetFieldDestination, index: number): void {
        if (!this.canAddUtmMapping(field)) {
            return;
        }
        const newField = new TargetFieldDestination();
        newField.updateWithSourceField(
            {
                code: field.code,
                name: field.name,
                exampleValues: field.exampleValues,
            } as any,
            field.groupe || 0
        );
        newField.tableDest = field.tableDest;
        newField.utmMatching = true;
        newField.utmPattern = field.utmPattern ?? null;
        newField.utmRegexMode = field.utmRegexMode || 'PRESET';
        newField.utmRegex = '';
        newField.utmType = null;
        newField.utmRules = [this.createDefaultUtmRule(newField)];
        this.fields.splice(index + 1, 0, newField);
        this.fields = [...this.fields];
        this.rebuildFieldGroups();
        this.updateValidate();
    }

    addUtmRule(field: TargetFieldDestination): void {
        this.ensureUtmRules(field);
        field.utmRules.push(this.createDefaultUtmRule(field, field.utmRules.length));
        this.enforceUtmRuleConstraints(field);
        field.utmRules = [...field.utmRules];
        this.updateValidate();
    }

    removeUtmRule(field: TargetFieldDestination, index: number): void {
        this.ensureUtmRules(field);
        if (field.utmRules.length <= 1) {
            return;
        }
        field.utmRules.splice(index, 1);
        this.enforceUtmRuleConstraints(field);
        field.utmRules = [...field.utmRules];
        this.updateValidate();
    }

    private rebuildFieldGroups(): void {
        this.useUtmGrouping = this.onlyUtmFields && (this.fields || []).length > 1;
        if (!this.useUtmGrouping) {
            this.fieldGroups = (this.fields || []).map((field, index) => ({
                key: `single-${index}`,
                label: field?.name || field?.code || `source-${index + 1}`,
                fields: [field],
            }));
            return;
        }
        const groups: Array<{ key: string; label: string; fields: TargetFieldDestination[] }> = [];
        (this.fields || []).forEach((field, index) => {
            const sourceKey = (field?.code || field?.name || `source-${index}`).toLowerCase();
            const found = groups.find((g) => g.key === sourceKey);
            if (found) {
                found.fields.push(field);
            } else {
                groups.push({
                    key: sourceKey,
                    label: field?.name || field?.code || `Source ${index + 1}`,
                    fields: [field],
                });
            }
        });
        this.fieldGroups = groups;
    }

    getFieldIndex(field: TargetFieldDestination): number {
        return (this.fields || []).indexOf(field);
    }

    getExcludedDestinationKeys(currentField: TargetFieldDestination): string[] {
        return (this.fields || [])
            .filter((f) => f !== currentField)
            .filter((f) => !!f.tableDest && !!f.fieldDest)
            .filter((f) => this.isUtmTargetField(f))
            .map((f) => this.getDestinationKey(f.tableDest, f.fieldDest));
    }

    canAddUtmMapping(currentField: TargetFieldDestination): boolean {
        if (!this.onlyUtmFields) {
            return false;
        }
        const tableDest = currentField?.tableDest || this.onlyTable;
        if (!tableDest) {
            return false;
        }
        const totalUtmFields = 5;
        const usedInTable = new Set(
            (this.fields || [])
                .filter((f) => (f.tableDest || '').toLowerCase() === (tableDest || '').toLowerCase())
                .filter((f) => this.isUtmTargetField(f))
                .map((f) => (f.fieldDest || '').toLowerCase())
        );
        return usedInTable.size < totalUtmFields;
    }

    canAddUtmMappingFromField(currentField: TargetFieldDestination): boolean {
        if (!currentField?.tableDest || !currentField?.fieldDest) {
            return false;
        }
        if (!this.canAddUtmMapping(currentField)) {
            return false;
        }
        const rules = Array.isArray(currentField.utmRules) ? currentField.utmRules : [];
        if (rules.length === 0) {
            return false;
        }
        return rules.every((rule) => {
            if (!rule) {
                return false;
            }
            if (rule.regexMode === 'DIRECT') {
                return true;
            }
            if (rule.regexMode === 'CUSTOM') {
                return !!(rule.regex && rule.regex.trim().length > 0);
            }
            return !!rule.utmPattern && !!rule.utmType;
        });
    }

    setField(fieldSource: ColFileStructure) {
        const field: TargetFieldDestination = this.fields[0];
        if (fieldSource) {
            field.name = fieldSource.name;
            field.code = fieldSource.code;
            field.exampleValues = fieldSource.exampleValues;
            // field.meta = fieldSource.meta;
        } else {
            field.name = null;
            field.code = null;
            field.exampleValues = null;
        }
    }

    isCodeEngine(field: TargetFieldDestination) {
        return field?.meta?.categoryField && [FieldTable.CategoryFieldEnum.IATACITY,
            FieldTable.CategoryFieldEnum.IATAAIRPORT,
            FieldTable.CategoryFieldEnum.IATACOUNTRY,
            FieldTable.CategoryFieldEnum.COUNTRY,
            FieldTable.CategoryFieldEnum.CARRIER,
            FieldTable.CategoryFieldEnum.AFFILIATION,
            FieldTable.CategoryFieldEnum.CIVILITY].includes(field.meta.categoryField);
    }

    defaultValueTraduction(field: TargetFieldDestination) {
        if (field.reverseSearch) {
            field.codeTraductionAiriane = this.traductionsAiriane[0].value;
        } else {
            field.codeTraductionAiriane = null;
        }
    }

    addReplaceValue(field: TargetFieldDestination): void {
        field.replaceRegex.push('');
        field.replaceValue.push('');
        field.replaceAll.push(false);
    }
    
    removeReplaceValue(field: TargetFieldDestination, index: number): void {
        field.replaceRegex.splice(index, 1);
        field.replaceValue.splice(index, 1);
        field.replaceAll.splice(index, 1);
    }

    getReplaceIndexes(field: TargetFieldDestination): number[] {
        return Array.from({ length: field.replaceRegex.length }, (_, i) => i);
    }

    isUtmTargetField(field: TargetFieldDestination): boolean {
        const f = (field?.fieldDest || '').toLowerCase();
        return ['provider_source', 'provider_medium', 'provider_campaign', 'provider_term', 'provider_content'].includes(f);
    }

    hasDirectRule(field: TargetFieldDestination): boolean {
        // A "DIRECT" rule is a catch-all: it always matches, so the "keep raw on no match"
        // fallback can never trigger and the option is meaningless once one is present.
        return (field.utmRules || []).some((rule) => rule?.regexMode === 'DIRECT');
    }

    getPresetRegexByUtmType(
        utmType: 'SOURCE' | 'MEDIUM' | 'CAMPAIGN' | 'TERM' | 'CONTENT',
        utmPattern: 'MISTERFLY_PATTERN_1' | 'MISTERFLY_PATTERN_2' = 'MISTERFLY_PATTERN_1'
    ): string {
        if (utmPattern === 'MISTERFLY_PATTERN_2') {
            if (utmType === 'CAMPAIGN') return '^(home)$';
            return '';
        }
        if (utmType === 'SOURCE') return '^.*src\\.([^~]*).*$';
        if (utmType === 'MEDIUM') return '^.*med\\.([^~]*).*$';
        if (utmType === 'CAMPAIGN') return '^.*cmp\\.([^~]*).*$';
        if (utmType === 'TERM') return '^.*trm\\.([^~]*).*$';
        return '^.*cnt\\.([^~]*).*$';
    }

    onUtmModeChange(field: TargetFieldDestination, rule: UtmRuleConfig): void {
        rule.regexMode = rule.regexMode || 'PRESET';
        if (rule.regexMode === 'PRESET') {
            if (!rule.utmPattern && this.utmPatterns.length === 1) {
                rule.utmPattern = this.utmPatterns[0].value;
            }
            rule.regex = '';
            if (rule.utmPattern) {
                this.applyDefaultUtmTypeForPattern(field, rule);
            } else {
                rule.utmType = null;
            }
        } else {
            rule.regex = rule.regex || '';
        }
        this.updateValidate();
    }

    onUtmPatternChange(field: TargetFieldDestination, rule: UtmRuleConfig): void {
        if (field.utmMatching && rule.regexMode === 'PRESET') {
            this.applyDefaultUtmTypeForPattern(field, rule);
        }
        this.enforceUtmRuleConstraints(field);
        this.updateValidate();
    }

    onUtmRuleModeChange(field: TargetFieldDestination, rule: UtmRuleConfig, mode: 'PRESET' | 'CUSTOM' | 'DIRECT'): void {
        rule.regexMode = mode;
        if (mode === 'DIRECT') {
            rule.regex = '';
            rule.utmPattern = null;
            rule.utmType = null;
        } else {
            this.onUtmModeChange(field, rule);
        }
        this.enforceUtmRuleConstraints(field);
        this.updateValidate();
    }

    private ensureUtmRules(field: TargetFieldDestination): void {
        if (!Array.isArray(field.utmRules) || field.utmRules.length === 0) {
            field.utmRules = [this.createDefaultUtmRule(field)];
        }
        field.utmRules = field.utmRules.map((rule) => ({
            utmPattern: rule.utmPattern || (this.utmPatterns.length === 1 && (rule.regexMode || (rule.regex ? 'CUSTOM' : 'PRESET')) === 'PRESET'
                ? this.utmPatterns[0].value
                : null),
            utmType: rule.utmType || (rule.regex ? null : (rule.utmPattern
                ? this.resolveUtmTypeByFieldDest(rule.utmPattern as 'MISTERFLY_PATTERN_1' | 'MISTERFLY_PATTERN_2', field.fieldDest)
                : null)),
            regex: rule.regex || '',
            groupIndex: rule.groupIndex || 1,
            regexMode: rule.regexMode || (rule.regex ? 'CUSTOM' : 'PRESET'),
        }));
        this.enforceUtmRuleConstraints(field);
    }

    private createDefaultUtmRule(field: TargetFieldDestination, index = 0): UtmRuleConfig {
        const hasSinglePattern = this.utmPatterns.length === 1;
        const presetSlotsLeft = this.getAvailablePatternsForNewRule(field).length;
        const shouldStartAsPreset = hasSinglePattern ? index === 0 : presetSlotsLeft > 0;
        const utmPattern = shouldStartAsPreset
            ? (hasSinglePattern ? this.utmPatterns[0].value : null)
            : null;
        const regexMode: 'PRESET' | 'CUSTOM' = shouldStartAsPreset ? 'PRESET' : 'CUSTOM';
        return {
            utmPattern: regexMode === 'PRESET' ? utmPattern : null,
            utmType: regexMode === 'PRESET'
                ? this.resolveUtmTypeByFieldDest((utmPattern || field.utmPattern || null) as 'MISTERFLY_PATTERN_1' | 'MISTERFLY_PATTERN_2', field.fieldDest)
                : null,
            regex: '',
            groupIndex: 1,
            regexMode,
        };
    }

    getAvailablePatterns(field: TargetFieldDestination, rule: UtmRuleConfig): SelectItem[] {
        if (this.utmPatterns.length <= 1) {
            return this.utmPatterns;
        }
        const selectedByOthers = new Set(
            (field.utmRules || [])
                .filter((r) => r !== rule && r.regexMode === 'PRESET' && !!r.utmPattern)
                .map((r) => r.utmPattern)
        );
        return this.utmPatterns.filter((p) => p.value === rule.utmPattern || !selectedByOthers.has(p.value));
    }

    private getAvailablePatternsForNewRule(field: TargetFieldDestination): SelectItem[] {
        const selectedByOthers = new Set(
            (field.utmRules || [])
                .filter((r) => r.regexMode === 'PRESET' && !!r.utmPattern)
                .map((r) => r.utmPattern)
        );
        return this.utmPatterns.filter((p) => !selectedByOthers.has(p.value));
    }

    getUtmTypeOptions(rule: UtmRuleConfig): SelectItem[] {
        const pattern = rule?.utmPattern || null;
        if (!pattern) {
            return [];
        }
        if (pattern === 'MISTERFLY_PATTERN_2') {
            return [{ label: 'Campaign Home (home)', value: 'CAMPAIGN' }];
        }
        return this.utmTypes;
    }


    private enforceUtmRuleConstraints(field: TargetFieldDestination): void {
        if (!Array.isArray(field.utmRules) || field.utmRules.length === 0) {
            return;
        }
        const uniquePresetPatterns = new Set<string>();
        field.utmRules = field.utmRules.map((rule, index) => {
            const next = {...rule};
            if (next.regexMode === 'PRESET') {
                if (this.utmPatterns.length === 1 && !next.utmPattern) {
                    next.utmPattern = this.utmPatterns[0].value;
                }
                if (!next.utmPattern) {
                    next.utmType = null;
                } else if (uniquePresetPatterns.has(next.utmPattern)) {
                    // Keep user data stable: resolve duplicate by demoting this rule to custom.
                    next.regexMode = 'CUSTOM';
                } else {
                    uniquePresetPatterns.add(next.utmPattern);
                    const options = this.getUtmTypeOptions(next);
                    if (options.length === 1) {
                        next.utmType = options[0].value as 'SOURCE' | 'MEDIUM' | 'CAMPAIGN' | 'TERM' | 'CONTENT';
                    } else {
                        const resolved = this.resolveUtmTypeByFieldDest(
                            next.utmPattern as 'MISTERFLY_PATTERN_1' | 'MISTERFLY_PATTERN_2',
                            field.fieldDest
                        );
                        next.utmType = resolved || next.utmType || null;
                    }
                    next.regex = '';
                }
            }
            return next;
        });
    }

    private applyDefaultUtmTypeForPattern(field: TargetFieldDestination, rule: UtmRuleConfig): void {
        if (!rule?.utmPattern) {
            rule.utmType = null;
            return;
        }
        const options = this.getUtmTypeOptions(rule);
        if (options.length === 1) {
            rule.utmType = options[0].value as 'SOURCE' | 'MEDIUM' | 'CAMPAIGN' | 'TERM' | 'CONTENT';
            return;
        }
        rule.utmType = this.resolveUtmTypeByFieldDest(
            rule.utmPattern as 'MISTERFLY_PATTERN_1' | 'MISTERFLY_PATTERN_2',
            field.fieldDest
        ) || null;
    }

    private resolveUtmTypeByFieldDest(
        pattern: 'MISTERFLY_PATTERN_1' | 'MISTERFLY_PATTERN_2',
        fieldDest: string
    ): 'SOURCE' | 'MEDIUM' | 'CAMPAIGN' | 'TERM' | 'CONTENT' | null {
        if (!pattern) {
            return null;
        }
        const byPattern: Record<string, Record<string, 'SOURCE' | 'MEDIUM' | 'CAMPAIGN' | 'TERM' | 'CONTENT'>> = {
            MISTERFLY_PATTERN_1: {
                provider_source: 'SOURCE',
                provider_medium: 'MEDIUM',
                provider_campaign: 'CAMPAIGN',
                provider_term: 'TERM',
                provider_content: 'CONTENT',
            },
            MISTERFLY_PATTERN_2: {
                provider_campaign: 'CAMPAIGN',
            },
        };
        const map = byPattern[pattern];
        if (!map) {
            return null;
        }
        return map[(fieldDest || '').toLowerCase()] || null;
    }

    private getDestinationKey(tableDest: string, fieldDest: string): string {
        return `${(tableDest || '').toLowerCase()}::${(fieldDest || '').toLowerCase()}`;
    }
}
